import * as React from "react";
import { Dispatch } from "react";
import { styled, SvgIconProps, Theme } from "@mui/material";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import {
  CONDENSED_FONT_LT,
  CondensedTypography,
  CondensedTypographyMedium,
} from "fe-web-components/styles/typography";
import { getQFAndRefinementsLines } from "fe-common/logic";
import { QualifiedFeature } from "fe-common/graphql";
import { AgentTooltip } from "fe-common/ui/AgentTooltip";
import { FindingStrength } from "fe-common/utils/FindingStrengthCalculator";
import { MobileBreakpoint } from "../layout/breakpoints";
//region [[ Styles ]]

const SupportingWeakeningWidgetView = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  width: "100%",
}));

const FeaturesList = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  padding: "0 25px",
  width: "50%",
  "&:nth-of-type(even)": {
    borderLeft: `1px solid #e4e4e4`,
    paddingRight: 0,
  },
  "&:nth-of-type(odd)": {
    paddingLeft: 0,
  },
}));

const FeatureItem = styled("div")({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "flex-start",
  margin: "2px 0",
});

const FeatureItemText = styled("span")(
  ({ theme, wide }: { theme?: any; wide?: boolean }) => ({
    width: wide ? "200px" : "250px",
    [theme.breakpoints.down(MobileBreakpoint)]: {
      width: "130px",
    },
    fontSize: "16px",
  }),
);

export interface StrengthIconProps extends SvgIconProps {
  strength: number;
  supporting: string;
}
export const StrengthIcon = styled("span")(
  ({ theme, supporting, strength }: { theme?: Theme } & StrengthIconProps) => ({
    width: 0,
    height: 0,
    marginBottom: "4px",
    marginRight: "8px",
    borderStyle: "solid",
    borderWidth: supporting === "true" ? "0 8px 16px 8px" : "16px 8px 0 8px",
    borderColor:
      supporting === "true"
        ? "transparent transparent " + KahunColorMap.Primary + " transparent"
        : KahunColorMap.Accent + " transparent transparent transparent",
    opacity: `${25 + Math.abs(strength) * 74}%`,
  }),
);

const Finding = styled(CondensedTypography)(
  ({ theme, wide }: { theme?: any; wide?: boolean }) => ({
    color: KahunColorMap.Primary,
    fontSize: "14px",
    fontFamily: "inherit",
    overflow: "hidden",
    textOverflow: "ellipsis",
    textAlign: "left",
    overflowWrap: "anywhere",
    fontWeight: 400,
  }),
);

const ClickableFinding = styled(Finding)({
  cursor: "pointer",
  transition: "color 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
  "&:hover": {
    color: KahunColorMap.Secondary,
  },
});

const FindingQualifier = styled(Finding)({
  color: KahunColorMap.Primary,
  fontSize: "12px",
});

const Headline = styled(CondensedTypographyMedium)(({ theme }) => ({
  fontFamily: "inherit",
  fontWeight: "bold",
  marginBottom: theme.spacing(1),
  [theme.breakpoints.down(MobileBreakpoint)]: {
    marginBottom: theme.spacing(1),
  },
  fontSize: "16px",
}));
//endregion [[ Styles ]]

//region [[ Props ]]

export interface SupportingWeakeningWidgetProps {
  findingsStrength: FindingStrength[];
  onSelect?: Dispatch<QualifiedFeature>;
  hideWeakening?: boolean;
}

//endregion [[ Props ]]

//region [[ Functions ]]
const renderFeatures = (
  features: FindingStrength[],
  title: string,
  onSelect: Dispatch<QualifiedFeature>,
  clickable: boolean,
) => {
  const FindingText = clickable && onSelect ? ClickableFinding : Finding;

  const renderRefinementsList = ({
    fs,
    findingQualifiersText,
  }: {
    fs: FindingStrength;
    findingQualifiersText: string;
  }) => (
    <FeatureItem key={`${fs.qf.id}-qualifiers`}>
      <AgentTooltip title={findingQualifiersText}>
        <FeatureItemText>
          <FindingQualifier>{findingQualifiersText}</FindingQualifier>
        </FeatureItemText>
      </AgentTooltip>
    </FeatureItem>
  );

  return (
    <FeaturesList className={"featuresList"}>
      <FeatureItem key={"headline"}>
        <Headline display={"inline"}>{title}</Headline>
      </FeatureItem>
      {features.map((fs) => {
        const [findingTitle, findingQualifiersText] = getQFAndRefinementsLines(
          fs.qf,
        );
        return (
          <React.Fragment key={fs.qf.id}>
            <FeatureItem key={fs.qf.id}>
              <StrengthIcon
                supporting={fs.strength > 0 ? "true" : "false"}
                strength={fs.strength}
              />
              <AgentTooltip title={findingTitle}>
                <FeatureItemText>
                  <FindingText
                    onClick={() => clickable && onSelect && onSelect(fs.qf)}
                  >
                    {findingTitle}
                  </FindingText>
                </FeatureItemText>
              </AgentTooltip>
            </FeatureItem>
            {renderRefinementsList({ fs, findingQualifiersText })}
          </React.Fragment>
        );
      })}
    </FeaturesList>
  );
};
//endregion [[ Functions ]]

export const SupportingWeakeningWidget = ({
  findingsStrength,
  onSelect,
  hideWeakening,
}: SupportingWeakeningWidgetProps) => {
  const [supporting, weakening] = [
    findingsStrength
      .filter((fs) => fs.strength > 0)
      .sort((b, a) => a.strength - b.strength),
    findingsStrength
      .filter((fs) => fs.strength < 0)
      .sort((a, b) => a.strength - b.strength),
  ];
  return (
    <SupportingWeakeningWidgetView>
      {supporting.length > 0 &&
        renderFeatures(supporting, "Supporting", onSelect, true)}
      {weakening.length > 0 &&
        !hideWeakening &&
        renderFeatures(weakening, "Weakening", onSelect, false)}
    </SupportingWeakeningWidgetView>
  );
};
